// Core data models matching the backend API

export interface Session {
  id: string;
  appName: string;        // camelCase
  userId: string;         // camelCase
  state: Record<string, any>;
  events: Event[];
  lastUpdateTime: string; // camelCase, ISO datetime string
  title?: string | null;  // Top-level field
  createdAt?: string | null; // camelCase, ISO datetime string
}

export interface Event {
  id: string;
  author: string;
  content?: Content;
  actions?: EventActions;
  timestamp: string;
  invocationId?: string;
  errorCode?: string;
  customMetadata?: Record<string, unknown>;
  groundingMetadata?: GroundingMetadata;
}

// Grounding metadata types for source citations
export interface GroundingMetadata {
  groundingChunks: GroundingChunk[];
  groundingSupports: GroundingSupport[];
  retrievalQueries?: string[];
}

export interface GroundingChunk {
  retrievedContext?: {
    ragChunk?: {
      text: string;
    };
    title?: string;
    uri?: string;
    text?: string;
  };
  web?: {
    title?: string;
    uri?: string;
  };
}

export interface GroundingSupport {
  segment: {
    startIndex: number;
    endIndex: number;
    text: string;
  };
  groundingChunkIndices: number[];
  confidenceScores?: number[];
}

// Text segment types for rendering
export interface TextSegment {
  type: 'text' | 'citation';
  content: string;
  sources?: SourceInfo[];
}

export interface SourceInfo {
  title: string;
  url: string;
  text?: string;
  confidence?: number;
  permalink?: string;
}

export interface Content {
  role: 'user' | 'model' | 'assistant';
  parts: Part[];
}

export interface Part {
  text?: string;
  thought?: boolean;
  functionCall?: FunctionCall;
  functionResponse?: FunctionResponse;
  inlineData?: InlineData;
}

export interface FunctionCall {
  id?: string;
  name: string;
  args: Record<string, any>;
}

export interface FunctionResponse {
  id?: string;
  name: string;
  response: any;
}

export interface InlineData {
  mimeType: string;
  data: string;
}

export interface EventActions {
  artifactDelta?: Record<string, number>;
  stateDelta?: Record<string, any>;
}

export interface Artifact {
  name: string;
  version: number;
  mimeType: string;
  data: string | ArrayBuffer;
  metadata?: Record<string, any>;
}

export interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  artifacts?: Artifact[];
  feedback?: FeedbackStatus;
  groundingMetadata?: GroundingMetadata;
}

export interface FeedbackStatus {
  eventId: string;
  type: 'like' | 'dislike' | null;
  timestamp?: Date;
}

export interface AgentRunRequest {
  sessionId?: string; // Optional - will create new session if not provided
  newMessage: Content;
  streaming?: boolean;
  toolConfig?: {
    corpus_ids: string[];
    enable_google_search: boolean;
    model: string;
  };
}

export interface AgentRunResponse {
  sessionId: string;
  events: Event[];
}

export interface HealthStatus {
  status: string;
  agentDir: string;
  environment: Record<string, string>;
  cachedAgents: string[];
  cachedRunners: string[];
}

// Corpus-related types

export interface CorpusFile {
  displayName: string;
  presignedUrl: string | null;
}

export interface CorpusData {
  corpusName: string;
  fileCount: number;
  files: CorpusFile[];
  type: 'company' | 'shared';
  description: string;
}

export interface CorpusResponse {
  company: CorpusData | null;
  shared: CorpusData | null;
  resolvedAt: string;
}

// Frontend-specific types

export interface AppState {
  user: {
    id: string;
    name: string;
    authenticated: boolean;
  };
  sessions: {
    list: Session[];
    current: Session | null;
    loading: boolean;
    error: string | null;
    isInWelcomeState: boolean;
  };
  chat: {
    messages: Message[];
    streaming: boolean;
    typing: boolean;
    error: string | null;
  };
  actions: {
    list: Action[];
    selectedAction: Action | null;
    loading: boolean;
    error: string | null;
    corpusFiles: ActionCorpusFile[];
    corpusFilesLoading: boolean;
  };
  ui: {
    sidebarOpen: boolean;
    theme: 'light' | 'dark';
    mobile: boolean;
  };
}

export enum ErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  SESSION_NOT_FOUND = 'SESSION_NOT_FOUND',
  AGENT_ERROR = 'AGENT_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

// Action Management Types

export interface ActionDetail {
  actionName: string;
  prompt: string;
  fileReference: {
    fileName: string;
    fileId: string;
  };
  additionalRecipients: string[];
  emailConfig: {
    recipientEmail: string;
    subjectTemplate: string;
    sendEnabled: boolean;
  };
  executionConfig: {
    timezone: string;
    preferredTime: string;
    retryAttempts: number;
  };
  metadata: {
    createdBy: string;
    description?: string;
    tags: string[];
  };
}

export interface Action {
  id: string;
  companyId: string;
  userId: string;
  isActive: boolean;
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  createdAt: string;
  updatedAt: string;
  actionDetail: ActionDetail;
}

export interface CreateActionRequest {
  actionName: string;
  prompt: string;
  fileName: string | string[]; // Support both single and multiple files
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  additionalRecipients?: string[];
  isActive?: boolean;
}

export interface UpdateActionRequest {
  actionName?: string;
  prompt?: string;
  fileName?: string | string[]; // Support both single and multiple files
  frequency?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  additionalRecipients?: string[];
  description?: string;
  isActive?: boolean;
}

export interface ActionListResponse {
  actions: Action[];
  totalCount: number;
}

// Extended CorpusFile interface for action file selection
export interface ActionCorpusFile {
  id: string;
  name: string;
  displayName: string;
}

// Action Run History Types

export interface ActionRunDetail {
  triggerType: 'scheduled' | 'manual';
  triggeredBy: string;
  inputFiles: string[];
  output: Record<string, any>;
  emailSent: boolean;
  emailRecipients: string[];
  agentResponse: Record<string, any>;
  metadata: Record<string, any>;
}

export interface ActionRun {
  id: string;
  actionId: string | null;  // Nullable for test runs
  companyId: string;
  userId: string;
  runType: 'scheduled' | 'manual' | 'test';  // Type of run
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: string;
  completedAt: string | null;
  durationSeconds: number | null;
  runDetail: ActionRunDetail;
  errorMessage: string | null;
  errorCode: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActionRunListResponse {
  runs: ActionRun[];
  totalCount: number;
}

export interface ActionRunStatistics {
  actionId: string;
  totalRuns: number;
  successfulRuns: number;
  failedRuns: number;
  runningRuns: number;
  avgDurationSeconds: number | null;
  lastRunAt: string | null;
  firstRunAt: string | null;
}

export interface TestActionResponse {
  status: string;
  message: string;
  timestamp: string;
}
